/* ČÁSTKA A JEJÍ JEDNOTKA — jeden nezlomitelný celek.
 *
 * PROČ TENHLE SOUBOR VZNIKL (nález majitele 8/2026)
 * Na `/fin-matematika` ve vietnamštině vypadaly souhrnné karty takhle:
 *
 *     KHOẢN TRẢ HẰNG THÁNG
 *     3 000 000
 *     Kč                     ← jednotka spadla pod číslo
 *
 * Číslo a jednotka byly jeden obyčejný text („3 000 000 Kč“) s ASCII mezerou
 * (U+0020) před jednotkou. Jakmile se karta zúžila, prohlížeč tu mezeru použil
 * jako místo zlomu. Vietnamsky je to horší, protože popisky jsou delší a berou
 * výšku i šířku — ale stejná díra je i česky, jen se otevře o pár pixelů dřív.
 *
 * ⚠️ TISÍCE SE NELÁMALY NIKDY. `toLocaleString("cs-CZ")` vrací mezi tisíci
 * U+00A0 (nezlomitelnou mezeru) — ověřeno. Lámala se JEN mezera před jednotkou,
 * protože ji kód psal ručně jako `" " + symbol`.
 *
 * CO SE S TÍM DĚLÁ — dvě vrstvy, obě povinné:
 *
 *   1. TEXT: `sJednotkou()` spojí číslo a jednotku U+00A0. Tím je jednotka
 *      neoddělitelná VŠUDE — ve větách, tooltipech, tabulkách, v PDF.
 *      Tohle je ta levná a globální polovina opravy.
 *
 *   2. VIZUÁL: komponenta `components/Castka.js` velké číslo navíc rozdělí na
 *      číslo + jednotku menším písmem a zastropuje velikost podle šířky karty
 *      (container query), takže se dlouhé číslo zmenší místo přetečení.
 *
 * MĚNA SE TU NEŘEŠÍ. Symbol přichází ze země tenanta (`lib/tenantMena.js`),
 * tenhle soubor ho jen sází. Nikdy odsud neodvozuj měnu z jazyka.
 */

/** Nezlomitelná mezera. Stejný znak, jaký dává `toLocaleString` mezi tisíce. */
export const NBSP = " ";

/** Nejdelší koncovka, kterou ještě bereme jako „jednotku“. Delší už je věta
 *  („nekonečná“, „spravovaný majetek“) a ta se zmenšovat ani lepit nemá. */
const MAX_DELKA_JEDNOTKY = 12;

/** Jednotka je menší než číslo — ale ne tak malá, aby ji poradce neviděl.
 *  ⚠️ Deklarace musí stát NAD `sirkaVEm()`, kde slouží jako výchozí hodnota
 *  parametru. Konstanta pod funkcí by byla dopředná reference (TDZ) a padala
 *  by na „Cannot access … before initialization“. */
export const POMER_JEDNOTKY = 0.62;

/* Číselná část smí obsahovat jen znaménko, číslice a oddělovače. Tím se
 * „Rok 2024“ nerozdělí na „Rok 2024“ + nic a „2 000 Kč“ ano. Znaménka jsou
 * i typografická (− U+2212) a odhadová (~, <, >), protože je repo používá. */
const RE_CISELNA_CAST = /^[+\-−±~<>]?[\s  ]*\d[\d\s  .,]*$/;

const RE_CISLICE = /\d/;

/**
 * Spojí číslo a jednotku tak, aby je prohlížeč nikdy nerozdělil.
 * Používej všude místo `cislo + " " + symbol`.
 *
 * @param {string|number} cislo   už naformátované číslo („3 000 000“)
 * @param {string} jednotka       „Kč“, „€“, „%“, „let“, „Kč/měs“…
 */
export function sJednotkou(cislo, jednotka) {
  const c = String(cislo ?? "");
  const j = String(jednotka ?? "").trim();
  if (!j) return c;
  if (!c) return j;
  return c + NBSP + j;
}

/** Index poslední číslice v textu, nebo −1. */
function posledniCislice(s) {
  for (let i = s.length - 1; i >= 0; i--) if (RE_CISLICE.test(s[i])) return i;
  return -1;
}

/**
 * Rozdělí hotový zobrazovaný text na číslo a jednotku.
 *
 * Pravidlo je záměrně mechanické: jednotka je všechno ZA poslední číslicí.
 * Nepotřebuje seznam měn ani jazyků, takže funguje pro Kč, €, %, „let“,
 * „rokov“, „năm“ i pro „1,5 mil. Kč“.
 *
 * Když se rozdělit nedá (žádná číslice — „—“, „♾️ nekonečná“; nebo je před
 * číslem text — „Rok 2024“; nebo je koncovka moc dlouhá), vrátí se
 * `delitelne: false` a volající musí text vysázet beze změny. NIKDY
 * nehádej — radši nerozdělený text než jednotka useknutá uprostřed.
 *
 * @returns {{cislo: string, jednotka: string, delitelne: boolean}}
 */
export function rozdelJednotku(text) {
  const s = String(text ?? "");
  const nedelitelne = { cislo: s, jednotka: "", delitelne: false };

  const i = posledniCislice(s);
  if (i < 0) return nedelitelne;

  const cislo = s.slice(0, i + 1);
  const jednotka = s.slice(i + 1).trim();
  if (!jednotka) return nedelitelne;
  if (jednotka.length > MAX_DELKA_JEDNOTKY) return nedelitelne;
  if (!RE_CISELNA_CAST.test(cislo)) return nedelitelne;

  return { cislo, jednotka, delitelne: true };
}

/* ŠÍŘKY ZNAKŮ V EM — základ pro strop velikosti písma.
 *
 * ⚠️ Hodnoty NEJSOU odhadnuté od stolu. První verze byla a mýlila se dost na
 * to, aby text přetekl: „%“ bylo tipnuté na 0,62 em, ve skutečnosti měří 0,92.
 * Změřeno v prohlížeči na `font-weight: 800` s `tabular-nums`:
 *   číslice 0,66 · mezera 0,20 · tečka/čárka 0,25 · % 0,92 · M/m 0,87
 *   K 0,67 · € 0,66 · č 0,56 · l 0,25 · t 0,37
 * 30. 9. 2026 appka přešla na JEDNU rodinu Inter Variable (index.css) a znovu
 * se měřilo (800, tabular-nums, 16 px = nejširší optická velikost; 28–72 px
 * vychází o chlup úžeji):
 *   číslice 0,645 · mezera i NBSP 0,269 · tečka/čárka 0,269 · % 1,023
 *   M 0,940 · m 0,924 · K 0,735 · € 0,690 · č 0,592 · l 0,281 · t 0,367
 * Inter má širší mezeru a % než Bricolage → EM_MEZERA a EM_SIROKE zvednuté,
 * ostatní hodnoty sedí s rezervou. Hlídá castka.test.js („nepodhodnotí Inter").
 * Zapsané hodnoty jsou o kousek VYŠŠÍ než naměřené — ze šířky se počítá strop
 * a nadhodnocení znamená o chlup menší, ale jistě se vejde. Podhodnocení
 * znamená přetečení, což je stejně špatné jako zalomení, kvůli kterému
 * tenhle soubor vznikl. */
const EM_CISLICE = 0.68;
const EM_MEZERA = 0.28; // Inter: mezera i NBSP 0,269 (Bricolage 0,20)
const EM_ODDELOVAC = 0.28; // tečka, čárka
const EM_SIROKE = 1.04; // % ‰ M m W w — výrazně širší; Inter % = 1,023 em
const EM_VELKE = 0.74; // velká písmena a symboly měn (K v „Kč“, €, $, £)
const EM_OSTATNI = 0.62; // malá písmena

const SIROKE = new Set(["%", "‰", "M", "m", "W", "w", "@"]);
/* Velké písmeno se pozná tím, že má jiný malý tvar — funguje pro každou
 * abecedu bez seznamu znaků. Rozsah [A-ZÁ-Ž] by nešel: mezi Á (U+00C1) a Ž
 * (U+017D) leží v Unicode i malá písmena, takže by „č“ dostalo šířku velkého. */
const MENY = new Set(["€", "$", "£", "₫", "zł"]);
const jeVelke = (z) => MENY.has(z) || (z.toLowerCase() !== z && z.toUpperCase() === z);

function sirkaTextuEm(s) {
  let w = 0;
  for (const z of String(s)) {
    if (RE_CISLICE.test(z)) w += EM_CISLICE;
    else if (z === " " || z === NBSP || z === " ") w += EM_MEZERA;
    else if (z === "." || z === ",") w += EM_ODDELOVAC;
    else if (SIROKE.has(z)) w += EM_SIROKE;
    else if (jeVelke(z)) w += EM_VELKE;
    else w += EM_OSTATNI;
  }
  return w;
}

/* Rezerva navrch. Šířky výše platí pro řez, na kterém se měřilo; aplikace
 * sází Inter Variable (do 30. 9. 2026 Bricolage Grotesque) a k tomu
 * přistupuje kerning a `letter-spacing`,
 * které součet znaků nezná. 6 % ten rozdíl pokryje a stojí jen pár pixelů
 * velikosti písma. Nesnižuj — přetečení je vidět, o 2 px menší číslo ne. */
const REZERVA_SIRKY = 1.06;

/**
 * Kolik em zabere „číslo + jednotka“, když je jednotka sázená menším písmem.
 * Slouží k výpočtu stropu velikosti písma podle šířky karty.
 *
 * @param {string} cislo
 * @param {string} jednotka
 * @param {number} pomerJednotky  velikost jednotky vůči číslu (0,62 = 62 %)
 */
export function sirkaVEm(cislo, jednotka, pomerJednotky = POMER_JEDNOTKY) {
  const zaklad = sirkaTextuEm(cislo);
  if (!jednotka) return zaklad;
  // NBSP + jednotka jdou menším písmem, takže se jejich šířka násobí poměrem.
  return zaklad + (EM_MEZERA + sirkaTextuEm(jednotka)) * pomerJednotky;
}

/**
 * CSS `font-size`, který nikdy nepřeteče šířku karty.
 *
 * Vrací `min(<návrhová velikost>, <šířka kontejneru / šířka textu>)`. Druhý
 * člen je v `cqw`, takže se počítá ze šířky nejbližšího container-query rodiče
 * — proto musí karta dostat `container-type: inline-size` (viz `stylKontejneru`
 * v components/Castka.js). Když ho nikdo nemá, `cqw` spadne na viewport, což je
 * neškodné: `min()` pak skoro vždy vybere návrhovou velikost.
 *
 * @param {string} zakladniVelikost  cokoli, co umí CSS („clamp(22px,3.4vw,34px)“)
 * @param {number} sirkaEm           z `sirkaVEm()`
 */
export function velikostDoSirky(zakladniVelikost, sirkaEm) {
  if (!zakladniVelikost) return undefined;
  if (!(sirkaEm > 0)) return zakladniVelikost;
  const cqw = (100 / (sirkaEm * REZERVA_SIRKY)).toFixed(2);
  return `min(${zakladniVelikost}, ${cqw}cqw)`;
}
